import type {CmLogger} from "@cm/log"
import {
    AnyImageOpContext,
    AnyImageOpResult,
    DisableImageOpNodeZodValidationInGraphTraversal,
    ImageOpNode,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {gatherImageOpsRecursively} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/image-op-utils"
import {ensureValidResult, EvaluatedParameters, Evaluator, GraphNode, NodeParameters, ParameterValue} from "@cm/graph-v3"
import {isArrayLike, isClass, isRecord, mapFields, promiseAllProperties} from "@cm/utils"

export const REF_COUNT_DEBUG_ID_ANY_IMAGE_OP_RESULT = "ImageOpNodeGraphEvaluator.anyImageOpResult"

export class ImageOpNodeGraphEvaluator<ImageOpContext extends AnyImageOpContext = AnyImageOpContext> implements Evaluator<ImageOpContext> {
    constructor(
        readonly context: ImageOpContext,
        private readonly logger: CmLogger,
    ) {}

    cleanUpReferences() {
        this.imageOpReferences.forEach((imageOp) => imageOp.release(REF_COUNT_DEBUG_ID_ANY_IMAGE_OP_RESULT))
        this.imageOpReferences.length = 0
    }

    async get<ReturnType, Parameters extends NodeParameters = NodeParameters>(
        node: ParameterValue<ReturnType, ImageOpContext, Parameters>,
    ): Promise<ReturnType> {
        return new Promise<ReturnType>((resolve) =>
            queueMicrotask(async () => {
                this.logger?.debug(`Getting parameter `, node)
                if (!(node instanceof GraphNode)) {
                    resolve(node)
                } else {
                    // check if we have a cached value for this parameter already
                    const cachedValue = this.cachedValueByNode.get(node)
                    if (cachedValue !== undefined) {
                        this.logger?.debug(`Found cached value for `, node, cachedValue)
                        const returnValue = cachedValue as ReturnType
                        // addRefImageOps(returnValue)
                        resolve(returnValue)
                    } else {
                        // check if we have a pending promise for this parameter already
                        const pendingPromise = this.pendingPromiseByNode.get(node) as Promise<ReturnType> | undefined
                        if (pendingPromise) {
                            const result = await pendingPromise
                            // TODO shouldn't we addRefImageOps(returnValue) here as well ?
                            resolve(result)
                        } else {
                            // create a new promise for this parameter and push it onto the stack
                            const evaluateGraph = async (node: GraphNode<unknown, ImageOpContext>) => {
                                if (!DisableImageOpNodeZodValidationInGraphTraversal) {
                                    node.ensureValidParameters()
                                }
                                const computedValue = await node.run(this)
                                if (DisableImageOpNodeZodValidationInGraphTraversal) {
                                    return computedValue
                                } else {
                                    return ensureValidResult(node, computedValue)
                                }
                            }
                            const promisedValue = evaluateGraph(node).then((value) => {
                                this.pendingPromiseByNode.delete(node)
                                // cache the value
                                this.cachedValueByNode.set(node, value)
                                // adjust ref-counts of children and dispose of them if necessary
                                // if (disposeFn) {
                                //     node.children.forEach((child) => {
                                //         let refCount = refCountByNode.get(child)
                                //         if (refCount === undefined) {
                                //             throw new Error(`Node ${child.constructor.name} not found in ref count map`)
                                //         }
                                //         if (refCount === 0) {
                                //             throw new Error(`Node ${child.constructor.name} reached a ref count of zero prematurely`)
                                //         }
                                //         refCount--
                                //         refCountByNode.set(child, refCount)
                                //         if (TRACE) {
                                //             console.log(`Decrementing ref-count of `, child, ` leaving ref-count of ${refCount}`)
                                //         }
                                //         if (refCount === 0) {
                                //             if (TRACE) {
                                //                 console.log(`Disposing `, child, cachedValueByNode.get(child))
                                //             }
                                //             const childValue = cachedValueByNode.get(child)
                                //             // there might be no value if the child has never been requested and therefore never been evaluated
                                //             // if (childValue === undefined) {
                                //             //     throw new Error(`Cached value not found for node ${child.constructor.name}`)
                                //             // }
                                //             disposeFn(child, childValue)
                                //         }
                                //     })
                                // }
                                return value
                            })
                            this.pendingPromiseByNode.set(node, promisedValue)
                            const result = await promisedValue
                            // keep track of ImageOpHal references returned by InstantiatingImageOpNode (they allocate a reference in their result)
                            if (node instanceof ImageOpNode) {
                                const imageOpsInResult: AnyImageOpResult[] = []
                                gatherImageOpsRecursively(result, imageOpsInResult)
                                imageOpsInResult.forEach((imageOp) => {
                                    imageOp.addRef(REF_COUNT_DEBUG_ID_ANY_IMAGE_OP_RESULT)
                                    this.imageOpReferences.push(imageOp)
                                })
                            }
                            // const traverseResult = (value: unknown) => {
                            //     if (Array.isArray(value)) {
                            //         value.forEach(traverseResult)
                            //     } else if (typeof value === "object" && value != null) {
                            //         if (value instanceof ImageOpHal || value instanceof ImageOpImgProc) {
                            //             this.imageOpReferences.push(value)
                            //         } else if (!isClass(value)) {
                            //             const obj = value as Record<string, unknown>
                            //             for (const key in obj) {
                            //                 traverseResult(obj[key])
                            //             }
                            //         }
                            //     }
                            // }
                            // traverseResult(result)
                            resolve(result as ReturnType)
                        }
                    }
                }
            }),
        )
    }

    getSync<ReturnType, Parameters extends NodeParameters = NodeParameters>(_value: ParameterValue<ReturnType, ImageOpContext, Parameters>): ReturnType {
        throw new Error("getSync is not implemented")
    }

    getAll<Parameters extends NodeParameters>(parameters: Parameters): Promise<EvaluatedParameters<Parameters, ImageOpContext>> {
        const traverse = (value: unknown): unknown => {
            if (Array.isArray(value)) {
                return Promise.all(value.map((value) => traverse(value)))
            } else if (value && !isClass(value) && (isRecord(value) || isArrayLike(value))) {
                return promiseAllProperties(mapFields(value, (value: unknown) => traverse(value)))
            } else {
                return this.get(value)
            }
        }
        return traverse(parameters) as Promise<EvaluatedParameters<Parameters, ImageOpContext>>
    }

    getAllSync<Parameters extends NodeParameters>(_parameters: Parameters): EvaluatedParameters<Parameters, ImageOpContext> {
        throw new Error("getAllSync is not implemented")
    }

    withContext<OtherContext extends ImageOpContext>(_context: OtherContext): Evaluator<OtherContext> {
        throw new Error("withContext is not implemented")
    }

    private cachedValueByNode = new Map<GraphNode<unknown, ImageOpContext>, unknown>()
    private pendingPromiseByNode = new Map<GraphNode<unknown, ImageOpContext>, Promise<unknown>>()
    readonly imageOpReferences: AnyImageOpResult[] = []
}
